/**
 * Deep merge utility for translation objects.
 * Recursively merges nested objects instead of overwriting them.
 * This ensures keys like "common.searchMembers" from common/translation-layer
 * are preserved when communities/translation-layer also defines "common".
 */
export function deepMerge(target: Record<string, any>, source: Record<string, any>): Record<string, any> {
  const result = { ...target }

  for (const key of Object.keys(source)) {
    if (
      source[key] &&
      typeof source[key] === 'object' &&
      !Array.isArray(source[key]) &&
      target[key] &&
      typeof target[key] === 'object' &&
      !Array.isArray(target[key])
    ) {
      result[key] = deepMerge(target[key], source[key])
    } else {
      result[key] = source[key]
    }
  }

  return result
}

export interface MergeLayerLocalesOptions {
  onError?: (args: { path: string; error: Error }) => void
}

export function mergeLayerLocales(
  layerLocales: Record<string, any>,
  currentLocale: string,
  initialMerged: Record<string, any> = {},
  options?: MergeLayerLocalesOptions
): Record<string, any> {
  let merged: Record<string, any> = { ...initialMerged }

  // Process layer locale files and deep merge translations on top
  for (const [path, module] of Object.entries(layerLocales)) {
    if (path.includes(`/${currentLocale}.json`)) {
      try {
        const translations = (module as any).default || module
        merged = deepMerge(merged, translations)
      } catch (e) {
        const error = e instanceof Error ? e : new Error(String(e))
        options?.onError?.({ path, error })
      }
    }
  }

  return merged
}
